import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, Spinner } from "@/components/common";
import { ImageUpload } from "@/components/ImageUpload";

function PasswordCard() {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/change-password", f); toast.success("Password updated"); setF({ current_password: "", new_password: "" }); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Panel className="p-6">
      <h3 className="mb-4 text-lg font-bold text-[#0B1A33]">Change password</h3>
      <form onSubmit={save} className="space-y-4">
        <div className="space-y-1.5"><Label>Current password</Label><Input required type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="profile-current-password" /></div>
        <div className="space-y-1.5"><Label>New password</Label><Input required minLength={8} type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="profile-new-password" /></div>
        <Button disabled={busy} variant="outline" className="rounded-full" data-testid="profile-password-save">{busy && <Spinner />} Update password</Button>
      </form>
    </Panel>
  );
}

export default function ProfileSettings() {
  const { user, setUser } = useAuth();
  const isTutor = user.role === "tutor";
  const [f, setF] = useState({ name: user.name, phone: user.phone || "", education_level: user.education_level || "",
    subject: user.subject || "", specialization: user.specialization || "", qualification: user.qualification || "",
    experience: user.experience || 0, bio: user.bio || "", profile_picture: user.profile_picture || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = isTutor ? { ...f, experience: Number(f.experience) || 0 } : { name: f.name, phone: f.phone, education_level: f.education_level, profile_picture: f.profile_picture };
      if (isTutor) delete body.education_level;
      const { data } = await api.put("/auth/profile", body);
      setUser({ ...user, ...data });
      toast.success("Profile saved");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const fields = isTutor
    ? [["subject", "Subject"], ["specialization", "Specialization"], ["qualification", "Qualification"], ["experience", "Experience (years)"]]
    : [["education_level", "Education level"]];
  return (
    <div>
      <PageHeader title="Profile Settings" subtitle="Keep your information up to date." />
      <div className="grid gap-6 lg:grid-cols-3">
        <Panel className="p-6 lg:col-span-2">
          <form onSubmit={save} className="space-y-5" data-testid="profile-form">
            <ImageUpload value={f.profile_picture} name={f.name} onChange={(v) => setF({ ...f, profile_picture: v })} />
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Full name</Label><Input value={f.name} onChange={set("name")} data-testid="profile-name-input" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input value={user.email} disabled /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={f.phone} onChange={set("phone")} data-testid="profile-phone-input" /></div>
              {fields.map(([k, l]) => <div key={k} className="space-y-1.5"><Label>{l}</Label><Input value={f[k]} onChange={set(k)} data-testid={`profile-${k}-input`} /></div>)}
            </div>
            {isTutor && <div className="space-y-1.5"><Label>Bio</Label><Textarea rows={5} value={f.bio} onChange={set("bio")} data-testid="profile-bio-input" /></div>}
            <Button disabled={busy} className="rounded-full bg-[#1F6FFF] hover:bg-[#1450C9]" data-testid="profile-save-button">{busy && <Spinner />} Save changes</Button>
          </form>
        </Panel>
        <PasswordCard />
      </div>
    </div>
  );
}
